import { fmtNumber } from "@/components/Amount";   // Batch 11 · B7: 2 decimals everywhere
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import { WS_OPEN_PARAM } from "@/lib/worksheetLinks";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import MultiFileAttachmentsPanel from "@/components/MultiFileAttachmentsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Ref from "@/components/Ref";
import { asArray } from "@/lib/asArray";
import SaveCancelBar, { SCB_TEXT } from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import { useFormDraft } from "@/lib/useDrafts";
import { saveFailedText } from "@/lib/saveThenSend";   // STEP 11d · A9
import { confirmDiscard } from "@/lib/unsavedChanges";

const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
/** the saved nested value with the typed leaves on top (the server deep-flattens a PATCH into dotted $set paths) */
const deepMerge = (a, b) => {
  if (!isObj(a) || !isObj(b)) return b;
  const o = { ...a };
  Object.keys(b).forEach((k) => { o[k] = deepMerge(a[k], b[k]); });
  return o;
};

/**
 * Batch C · Round C2 · W8 — Estimates worksheet dialog.
 * K6 Fix List — approach (b) exposes Methods / Significant assumptions
 * / Data (description + evaluation + evidence each), approach (c) has
 * the auditor's point-estimate + range, Linked-risk is a picker from
 * the engagement's risk register, 207(a) is a link, PY outcome is
 * captured, disclosure of estimation uncertainty (Y/N + note), bias
 * Yes → note required, and Send-to-misstatements shows when there is
 * a difference.
 */
export default function EstimatesWorksheetDialog() {
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const ref = params.get(WS_OPEN_PARAM.estimate);
  const [doc, setDoc] = useState(null);
  const [priorYear, setPriorYear] = useState(null);   // Batch 10.1 · R2 — the prior year's estimates, read-only
  const [risks, setRisks] = useState([]);
  const [risksStatus, setRisksStatus] = useState("loading");   // loading|ok|error

  useEffect(() => {   // Batch 10.1 · R2 — the prior year (roll-forward link) for the read-only panel
    if (!ref || !currentEngId) return;
    api.get(`/engagements/${currentEngId}/prior-year`, { _silent: true }).then((r) => setPriorYear(r.data)).catch(() => setPriorYear(null));
  }, [ref, currentEngId]);
  useEffect(() => {
    if (!ref || !currentEngId) { setDoc(null); return; }
    api.get(`/engagements/${currentEngId}/estimates/ref/${ref}`, { _silent: true })
      .then((r) => setDoc(r.data)).catch(() => setDoc(null));
    // N1 fix — the risk register is at `/risk/{eng_id}/items` (returns
    // an array of `risk_items`). The old code called a non-existent
    // `/risk-register` endpoint, which always 404'd → the picker fell
    // through to the "register unavailable" text box even on
    // engagements with real risks. Track {ok, error, empty} explicitly.
    setRisksStatus("loading");
    api.get(`/risk/${currentEngId}/items`, { _silent: true })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data
                    : (r.data?.rows || r.data?.risks || []);
        setRisks(rows);
        setRisksStatus("ok");
      })
      .catch(() => { setRisks([]); setRisksStatus("error"); });
  }, [ref, currentEngId]);

  // L1 fix — every hook is called on every render (before the early
  // return). Previously `useMemo` sat below the `if (!ref) return null`
  // guard, so the hook count differed between the closed/open states
  // and React crashed with "Rendered more hooks than during the
  // previous render" (react-hooks/rules-of-hooks).
  const risksList = useMemo(() => asArray(risks).map((r) => ({
    id: r.id || r.ref || r.risk_id,
    // N1 — surface a short, human label (spec K6): FS area / assertion
    // then a truncated risk description.
    label: (() => {
      const fs = r.fs_area || r.title || "";
      const assertion = r.assertion || "";
      const desc = (r.risk_description || r.name || r.description || "").slice(0, 80);
      const head = [fs, assertion].filter(Boolean).join(" · ");
      return [head, desc].filter(Boolean).join(" — ") || (r.id || "risk");
    })(),
  })).filter((x) => x.id), [risks]);

  // Batch 28 · Follow-up 1 · F2 (a): the worksheet is a page-form draft — nothing is written while typing; Save sends the
  // changed fields in ONE PATCH; Cancel puts the saved values back (the inputs re-mount); Mark complete and Sign wait until
  // saved; closing the dialog asks first. Send to Misstatements and the attachments stay immediate actions.
  const fd = useFormDraft(doc);
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);

  if (!ref) return null;
  const close = async () => {
    if (!(await confirmDiscard(fd.dirty, lang))) return;
    const p = new URLSearchParams(params); p.delete(WS_OPEN_PARAM.estimate); setParams(p, { replace: true });
  };
  const patch = async (body) => {
    try { const r = await api.patch(`/engagements/${currentEngId}/estimates/ref/${ref}`, body); setDoc(r.data); return true; }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); return false; }
  };
  const dv = fd.value || {};
  const setTop = (obj) => fd.setMany(obj);
  const saveForm = async () => {
    if (!fd.dirty) return;
    setSaving(true);
    try { if (await patch(fd.patch)) { fd.reset(); toast.success(SCB_TEXT[lang === "ar" ? "ar" : "en"].savedOk); } }
    finally { setSaving(false); }
  };
  const cancelForm = () => { fd.reset(); setFormKey((k) => k + 1); };
  const patchIn = (section, fields) =>
    fd.set(section, deepMerge(dv[section] || {}, fields));
  // O1 fix — every field patch sends ONLY its leaf, not the merged
  // parent. The backend deep-flattens to dotted `$set` paths so
  // concurrent typing on Low / High / Point never clobbers siblings.
  const patchApproach = (key, fields) => patchIn("approach", { [key]: fields });
  const patchB = (block, fields) => patchIn("approach", { b_test_how_management: { [block]: fields } });
  const gate = doc?.can_mark_complete || { ok: false, reasons: [] };
  const ap = dv.approach || {};
  const b = ap.b_test_how_management || {};
  const c = ap.c_auditor_point || {};
  const py = dv.prior_period_outcome || {};
  const bias = dv.management_bias_indicators || {};
  const du = dv.disclosure_uncertainty || {};
  const diff = doc?.difference_vs_point;
  const YN = (val, on, tid) => (
    <select className="h-6 text-xs" value={val === null || val === undefined ? "" : String(val)}
             onChange={(e) => on(e.target.value === "true" ? true : e.target.value === "false" ? false : null)}
             data-testid={tid}>
      <option value="">—</option>
      <option value="true">{t("common.yes")}</option>
      <option value="false">{t("common.no")}</option>
    </select>
  );

  return (
    <FullScreenDialog open={true} onClose={close} testid="est-dialog"
                      title={<>{t("est.title")} — <Ref>{ref}</Ref></>}>
        {!doc ? (
          <div data-testid="est-loading">{t("common.loading")}</div>
        ) : (
          <div className="space-y-4 text-xs" key={formKey}>
            <SignOffBar itemType="estimate" itemKey={ref} refreshKey={doc.updated_at} saveFirst={fd.dirty}
                        onChange={() => api.get(`/engagements/${currentEngId}/estimates/ref/${ref}`, { _silent: true }).then((r) => setDoc(r.data)).catch(() => {})} />
            <section className="grid gap-2 md:grid-cols-2" data-testid="est-header">
              <div><Label>{t("est.estimateName")}</Label>
                <Input data-testid="est-name" defaultValue={dv.estimate_name || ""}
                  placeholder={t("est.estimatePlaceholder")}
                  onChange={(e) => setTop({ estimate_name: e.target.value })} /></div>
              <div><Label>{t("est.fsHead")}</Label>
                <div className="rounded border bg-slate-50 px-2 py-1" data-testid="est-fs">{doc.fs_code}</div></div>
              <div>
                <Label>{t("est.linkedRisk")}</Label>
                {risksStatus === "ok" && risksList.length > 0 ? (
                  <select className="h-8 w-full rounded border text-xs"
                          data-testid="est-risk-picker"
                          value={dv.linked_risk_id || ""}
                          onChange={(e) => setTop({ linked_risk_id: e.target.value })}>
                    <option value="">—</option>
                    {risksList.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                ) : risksStatus === "ok" && risksList.length === 0 ? (
                  <div className="text-xs text-slate-500" data-testid="est-risk-empty">
                    {t("est.linkedRiskEmpty")}
                  </div>
                ) : risksStatus === "error" ? (
                  <Input data-testid="est-risk-text" defaultValue={dv.linked_risk_id || ""}
                          placeholder={t("est.linkedRiskPh")}
                          onChange={(e) => setTop({ linked_risk_id: e.target.value })} />
                ) : (
                  <div className="text-xs text-slate-400" data-testid="est-risk-loading">
                    {t("common.loading")}
                  </div>
                )}
              </div>
              <div>
                <Label>{t("est.link207a")}</Label>
                <Link to={`/planning?openForm=207a`} className="text-xs text-blue-700 underline block"
                       data-testid="est-207a-link">
                  {t("est.open207a")}
                </Link>
                <Input className="mt-1" data-testid="est-207a" defaultValue={dv.link_207a || ""}
                        placeholder={t("est.link207aNotePh")}
                        onChange={(e) => setTop({ link_207a: e.target.value })} />
              </div>
              <div><Label>{t("est.managementPoint")}</Label>
                <Input type="number" data-testid="est-mgmt-point"
                  defaultValue={dv.management_point_estimate ?? ""}
                  onChange={(e) => setTop({ management_point_estimate: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
              <div><Label>{t("est.difference")}</Label>
                <div className="rounded border bg-slate-50 px-2 py-1" data-testid="est-diff">
                  {diff == null ? "—" : fmtNumber(diff)}
                </div></div>
            </section>

            {/* Approach — at least one of a/b/c */}
            <section className="rounded border p-3" data-testid="est-approach">
              <div className="mb-1 font-semibold">{t("est.approach.title")}</div>
              {[
                ["a_events_up_to_report", "a", "a"],
                ["b_test_how_management", "b", "b"],
                ["c_auditor_point", "c", "c"],
              ].map(([k, code]) => (
                <label key={k} className="me-4 inline-flex items-center">
                  <input type="checkbox" data-testid={`est-ap-${k}`}
                    checked={!!ap[k]?.used}
                    onChange={(e) => patchApproach(k, { used: e.target.checked })} />
                  &nbsp;{t(`est.approach.${code}`)}
                </label>
              ))}
              {ap.a_events_up_to_report?.used && (
                <div className="mt-2" data-testid="est-ap-a-block">
                  <Label>{t("est.approach.aNote")}</Label>
                  <Textarea data-testid="est-ap-a-note" defaultValue={ap.a_events_up_to_report?.note || ""}
                             onChange={(e) => patchApproach("a_events_up_to_report", { note: e.target.value })} />
                </div>
              )}
              {b.used && (
                <div className="mt-2 space-y-3" data-testid="est-ap-b-block">
                  {[["methods", "b_methods"], ["significant_assumptions", "b_assumptions"], ["data", "b_data"]].map(([blk, tkey]) => (
                    <div key={blk} className="rounded border p-2" data-testid={`est-ap-b-${blk}`}>
                      <div className="mb-1 font-medium">{t(`est.approach.${tkey}`)}</div>
                      <Label>{t("est.approach.description")}</Label>
                      <Textarea data-testid={`est-ap-b-${blk}-description`} defaultValue={(b[blk] || {}).description || ""}
                                 onChange={(e) => patchB(blk, { description: e.target.value })} />
                      <Label>{t("est.approach.evaluation")}</Label>
                      <Textarea data-testid={`est-ap-b-${blk}-evaluation`} defaultValue={(b[blk] || {}).evaluation || ""}
                                 onChange={(e) => patchB(blk, { evaluation: e.target.value })} />
                      <Label>{t("est.approach.evidence")}</Label>
                      <Input data-testid={`est-ap-b-${blk}-evidence`} defaultValue={(b[blk] || {}).evidence || ""}
                              onChange={(e) => patchB(blk, { evidence: e.target.value })} />
                    </div>
                  ))}
                </div>
              )}
              {c.used && (
                <div className="mt-2 grid gap-2 md:grid-cols-4" data-testid="est-ap-c-block">
                  <div><Label>{t("est.approach.low")}</Label>
                    <Input type="number" data-testid="est-ap-c-low"
                      defaultValue={c.low ?? ""}
                      onChange={(e) => patchApproach("c_auditor_point", { low: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                  <div><Label>{t("est.approach.high")}</Label>
                    <Input type="number" data-testid="est-ap-c-high"
                      defaultValue={c.high ?? ""}
                      onChange={(e) => patchApproach("c_auditor_point", { high: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                  <div><Label>{t("est.approach.point")}</Label>
                    <Input type="number" data-testid="est-ap-c-point"
                      defaultValue={c.point ?? ""}
                      onChange={(e) => patchApproach("c_auditor_point", { point: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                  <div><Label>{t("est.approach.basis")}</Label>
                    <Input data-testid="est-ap-c-basis"
                      defaultValue={c.basis || ""}
                      onChange={(e) => patchApproach("c_auditor_point", { basis: e.target.value })} /></div>
                </div>
              )}
            </section>

            {/* Prior-period outcome */}
            <section className="rounded border p-3" data-testid="est-py-outcome">
              <div className="mb-1 font-semibold">{t("est.priorPeriod")}</div>
              {priorYear?.available && (priorYear.estimate_worksheets || []).length > 0 && (
                /* Batch 10.1 · R2 — the prior year's estimates worksheets through the roll-forward link, READ-ONLY, for the
                   retrospective review of prior-year estimates (ISA 540 (Revised) para 14). Nothing is copied. */
                <div className="mb-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                     data-testid="est-prior-year-readonly">
                  <div className="mb-1 font-medium">
                    {t("est.priorYearReadOnly").replace("{fy}", String(priorYear.engagement?.financial_year || ""))}
                  </div>
                  <table className="w-full">
                    <thead><tr className="text-muted-foreground">
                      <th className="text-start font-normal">{t("est.priorYearName")}</th>
                      <th className="text-end font-normal">{t("est.priorYearPoint")}</th>
                      <th className="text-start font-normal ps-3">{t("est.priorYearConclusion")}</th>
                    </tr></thead>
                    <tbody>{asArray(priorYear.estimate_worksheets).map((w) => (
                      <tr key={w.id} data-testid={`est-prior-${w.ref || w.id}`}
                          className={w.estimate_name && doc?.estimate_name && w.estimate_name === doc.estimate_name ? "font-semibold" : ""}>
                        <td>{w.ref ? `${w.ref} · ` : ""}{w.estimate_name || "—"}</td>
                        <td className="text-end font-mono">{w.management_point_estimate != null && w.management_point_estimate !== ""
                          ? fmtNumber(w.management_point_estimate) : "—"}</td>
                        <td className="ps-3">{w.conclusion || "—"}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
              <div className="grid gap-2 md:grid-cols-3">
                <div><Label>{t("est.priorPeriodEstimate")}</Label>
                  <Input type="number" data-testid="est-py-estimate"
                    defaultValue={py.py_estimate ?? ""}
                    onChange={(e) => patchIn("prior_period_outcome", { py_estimate: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>{t("est.priorPeriodActual")}</Label>
                  <Input type="number" data-testid="est-py-actual"
                    defaultValue={py.actual ?? ""}
                    onChange={(e) => patchIn("prior_period_outcome", { actual: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                <div><Label>{t("est.priorPeriodNote")}</Label>
                  <Input data-testid="est-py-note" defaultValue={py.note || ""}
                          onChange={(e) => patchIn("prior_period_outcome", { note: e.target.value })} /></div>
              </div>
            </section>

            {/* Disclosure of estimation uncertainty */}
            <section className="rounded border p-3" data-testid="est-disclosure">
              <div className="mb-1 font-semibold">{t("est.disclosureUncertainty")}</div>
              {YN(du.evaluated,
                 (v) => patchIn("disclosure_uncertainty", { evaluated: v }),
                 "est-disclosure-eval")}
              <Textarea className="mt-2" data-testid="est-disclosure-note" defaultValue={du.note || ""}
                         onChange={(e) => patchIn("disclosure_uncertainty", { note: e.target.value })} />
            </section>

            {/* Bias indicators */}
            <section className="rounded border p-3" data-testid="est-bias">
              <div className="mb-1 font-semibold">{t("est.biasIndicators")}</div>
              <div className="flex gap-2">
                {[[true, "biasYes"], [false, "biasNo"]].map(([v, k]) => (
                  <button key={String(v)} type="button"
                    data-testid={`est-bias-${v ? "yes" : "no"}`}
                    className={`rounded border px-2 py-1 ${bias.present === v ? "bg-blue-100 border-blue-500" : ""}`}
                    onClick={() => patchIn("management_bias_indicators", { present: v })}>
                    {t(`est.${k}`)}
                  </button>
                ))}
              </div>
              {bias.present === true && (
                <Textarea className="mt-1" data-testid="est-bias-note" placeholder={t("est.biasNote")}
                  defaultValue={bias.note || ""}
                  onChange={(e) => patchIn("management_bias_indicators", { note: e.target.value })} />
              )}
            </section>

            {/* Send to misstatements (Judgmental) — O2 idempotent */}
            {diff != null && Math.abs(diff) >= 0.01 && (
              <div data-testid="est-ms-block">
                {doc.misstatement_sent ? (
                  <div className="inline-flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800"
                       data-testid="est-sent-ms">
                    <span>{t("est.sentToMsOk")}</span>
                    <a href={doc.misstatement_sent.link_back || "#"} target="_blank" rel="noopener"
                       className="underline" data-testid="est-sent-ms-link">
                      {t("common.viewSent")}
                    </a>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" data-testid="est-send-ms"
                    onClick={async () => {
                      // STEP 11d · A9 (find 30): unsaved changes are saved first and the difference AS SAVED is sent; a refused
                      // save shows its error and nothing is sent
                      let amount = diff;
                      if (fd.dirty) {
                        let saved = null;
                        try { saved = (await api.patch(`/engagements/${currentEngId}/estimates/ref/${ref}`, fd.patch, { _silent: true })).data; }
                        catch (e) { toast.error(saveFailedText(toErrMsg(e, "Save failed"), lang)); return; }
                        setDoc(saved); fd.reset();
                        amount = saved?.difference_vs_point;
                        if (amount == null || Math.abs(amount) < 0.01) return;
                      }
                      try {
                        await api.post(`/engagements/${currentEngId}/estimates/ref/${ref}/send-to-misstatements`, { amount });
                        toast.success(t("est.sentToMsOk"));
                        const g = await api.get(`/engagements/${currentEngId}/estimates/ref/${ref}`);
                        setDoc(g.data);
                      } catch (e) {      // Batch 29 · M65: already in Misstatements → offer the link
                        try {
                          if (await offerLink(e, { engId: currentEngId, lang })) {
                            const g = await api.get(`/engagements/${currentEngId}/estimates/ref/${ref}`); setDoc(g.data); return;
                          }
                        } catch (e2) { toast.error(toErrMsg(e2, "Send failed")); return; }
                        toast.error(toErrMsg(e, "Send failed"));
                      }
                    }}>{t("est.sendToMisstatements")}</Button>
                )}
              </div>
            )}

            {/* Round 11 · M1 — multi-file supporting attachments. */}
            <MultiFileAttachmentsPanel engId={currentEngId}
                                        contextType="estimate"
                                        contextId={ref}
                                        testid="est-mfa" />

            {/* Conclusion + sign-off */}
            <section className="rounded border p-3" data-testid="est-signoff">
              <Label>{t("est.conclusion")}</Label>
              <div className="mt-1 flex gap-2">
                {["reasonable", "misstated"].map((v) => (
                  <button key={v} type="button"
                    data-testid={`est-concl-${v}`}
                    className={`rounded border px-2 py-1 ${dv.conclusion === v ? "bg-blue-100 border-blue-500" : ""}`}
                    onClick={() => setTop({ conclusion: v })}>
                    {v === "reasonable" ? t("est.conclusionReasonable") : t("est.conclusionMisstated")}
                  </button>
                ))}
              </div>
              {!gate.ok && (
                <div className="mt-1 text-red-700" data-testid="est-guard-reasons">
                  {gate.reasons.map((r) => t(`est.reasons.${r}`) || r).join(" · ")}
                </div>
              )}
              <Button className="mt-2" data-testid="est-mark-complete"
                disabled={(!gate.ok && !doc.completed) || fd.dirty}
                onClick={() => patch({ completed: !doc.completed })}>
                {doc.completed ? "Unmark" : t("est.markComplete")}
              </Button>
            </section>
            <SaveCancelBar dirty={fd.dirty} saving={saving} onSave={saveForm} onCancel={cancelForm} lang={lang} testid="est-save-bar" />
          </div>
        )}
    </FullScreenDialog>
  );
}
